'use client';

import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { ResponsiveImage } from '@web/molecules';
import { Heading } from '@web/atoms';
import { useReporter } from '../../reporter';
import { userViewsCrunchingNumbersPage } from '../events';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import {
  StyledCrunchingNumbers,
  LogoHeadingWrapper,
  LoadingBarContainer,
  LoadingBarProgress,
  Slideshow,
  Slide,
  CaptionText,
} from './styles';

export const CrunchingNumbers = ({
  ollieLogo,
  headline,
  fontColor = 'text.lightest',
  backgroundColor = 'hero.plum',
  loadingBarBgColor,
  loadingBarFillColor = 'hero.plum',
  images,
  captions,
  slideDuration,
  onLoadingComplete,
  eventVariationId = '',
}) => {
  const reporter = useReporter();
  const { formattedAmount } = useShopifyOfferContext();
  useEffect(() => {
    reporter.tag(userViewsCrunchingNumbersPage(eventVariationId));
  }, [reporter, eventVariationId]);

  return (
    <StyledCrunchingNumbers
      data-testid="onboarding-plan-loading"
      $backgroundColor={backgroundColor}
      $fontColor={fontColor}
    >
      <LogoHeadingWrapper>
        <ResponsiveImage priority={true} {...ollieLogo} width={100} />
        {headline && (
          <Heading
            fontColor={fontColor}
            backgroundColor={backgroundColor}
            headingText={interpolate(headline, {
              offer: formattedAmount ?? '50%',
            })}
            position="center"
            typography="heading3"
          ></Heading>
        )}
      </LogoHeadingWrapper>
      <LoadingBarContainer $loadingBarBgColor={loadingBarBgColor}>
        <LoadingBarProgress
          $loadingBarFillColor={loadingBarFillColor}
          $duration={images.length * slideDuration}
          onAnimationEnd={() => {
            onLoadingComplete();
          }}
        ></LoadingBarProgress>
      </LoadingBarContainer>
      <Slideshow>
        {images.map((img, i) => (
          <Slide
            key={i}
            $slideDuration={slideDuration}
            $delay={i * slideDuration}
            $isLast={i === images.length - 1}
          >
            <ResponsiveImage priority={true} {...img} />
            <CaptionText $fontColor={fontColor}>
              {interpolate(captions[i], { offer: formattedAmount ?? '50%' })}
            </CaptionText>
          </Slide>
        ))}
      </Slideshow>
    </StyledCrunchingNumbers>
  );
};

CrunchingNumbers.displayName = 'CrunchingNumbers';
CrunchingNumbers.propTypes = {
  backgroundColor: PropTypes.string,
  headline: PropTypes.string,
  ollieLogo: PropTypes.object,
  images: PropTypes.array,
  captions: PropTypes.array,
  onLoadingComplete: PropTypes.func,
  eventVariationId: PropTypes.string,
};
